import moment from "moment/moment";
import { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import { toast } from "react-toastify";
import {
  Users,
  UserCheck,
  GraduationCap,
  Clock,
  Search,
  Plus,
  Download,
  FileSpreadsheet,
  Mail,
  Edit3,
  Trash2,
  Calendar,
  ChevronDown,
  MoreVertical,
  CheckSquare,
  Square,
  ChevronRight,
  User,
  Eye,
  RefreshCcw,
  SlidersHorizontal,
  X,
  AlertTriangle
} from "lucide-react";

import { CourseBuilderStaticData } from "../../../_constants/staticData";
import { downloadCSV } from "../../../_helpers";
import axiosInstance from "../../../axios";
import { Loading } from "../../Other/Loading";
import Modal from "../../Other/Modal";
import BulkUploadOverlay from "../Shared/BulkUpload/BulkUploadOverlay";
import "./CourseList.scss";
import Forms from "./MemberList/Forms";
import Pagination from "./MemberList/Pagination";

function InstructorStudentList() {
  const [studentData, setStudentData] = useState([]);
  const [studentStats, setStudentStats] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [studentId, setStudentId] = useState(0);
  const [dateRangePicker, setDateRangePicker] = useState(false);
  const [modal, setModal] = useState(false);
  const [dateToggle, setDateToggle] = useState(false);
  const [editStudentData, setEditStudentData] = useState([]);
  const [addBulkUsers, setAddBulkUsers] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  // Advanced Filters state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all, ongoing, passed
  const [filter, setFilter] = useState({
    status: "",
    startDate: "",
    endDate: "",
    page: 1,
    itemsPerPage: 10,
  });

  // Action Menu dropdown tracker per row
  const [openActionRowId, setOpenActionRowId] = useState(null);

  // Row selection checkboxes state
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const refOne = useRef(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("instructor/students?perPage=999999");
      const data = res.data?.data?.data || [];
      setStudentData(data);
      
      const totalStudents = data.length;
      const ongoingStudents = data.filter((student) => student.isActive === 1).length;
      const passedStudents = data.filter((student) => student.isActive === 0).length;

      // Calculate recently joined count (within past 30 days)
      const thirtyDaysAgo = moment().subtract(30, "days");
      const recentlyJoined = data.filter((student) => 
        student.createdAt && moment(student.createdAt).isAfter(thirtyDaysAgo)
      ).length;

      setStudentStats({ 
        totalStudents, 
        ongoingStudents, 
        passedStudents,
        recentlyJoined 
      });
    } catch (error) {
      console.error("Error fetching student data:", error);
      toast.error("Failed to load student directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStudentStats = async () => {
      try {
        const res = await axiosInstance.get("admin/student-statistics");
        if (res.data?.data) {
          setStudentStats(prev => ({
            ...prev,
            ...res.data.data
          }));
        }
      } catch (error) {
        console.error("Error fetching student statistics:", error);
      }
    };

    fetchStudentStats();
  }, [isUploaded]);

  useEffect(() => {
    fetch();
    document.addEventListener("keydown", hideOnEscape, true);
    document.addEventListener("click", hideOnClickOutside, true);
    return () => {
      document.removeEventListener("keydown", hideOnEscape, true);
      document.removeEventListener("click", hideOnClickOutside, true);
    };
  }, [filter.page, filter.itemsPerPage, filter.status, filter.startDate, filter.endDate, modal, confirmDelete, isUploaded]);

  const handleSelectChange = (e) => {
    setFilter({ ...filter, itemsPerPage: Number(e.target.value), page: 1 });
  };

  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const hideOnEscape = (e) => {
    if (e.key === "Escape") {
      setDateToggle(false);
      setOpenActionRowId(null);
    }
  };

  const hideOnClickOutside = (e) => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      setDateToggle(false);
    }
  };

  const handleDateRangeFilter = (item) => {
    setFilter({
      ...filter,
      startDate: moment(item.selection.startDate).format("YYYY-MM-DD"),
      endDate: moment(item.selection.endDate).format("YYYY-MM-DD"),
      page: 1,
    });
    setRange([item.selection]);
  };

  const clearDateFilter = (e) => {
    e.stopPropagation();
    setFilter({
      ...filter,
      startDate: "",
      endDate: "",
      page: 1
    });
    setDateRangePicker(false);
    setDateToggle(false);
  };

  const deleteStudent = (userId) => {
    setConfirmDelete(true);
    setStudentId(userId);
  };

  const handleDeleteStudent = async () => {
    try {
      if (studentId === "bulk") {
        // Bulk delete execution
        for (const id of selectedStudentIds) {
          await axiosInstance.post("admin/user/delete/", { userId: id });
        }
        setSelectedStudentIds(new Set());
        toast.success(`${selectedStudentIds.size} students removed successfully`);
      } else {
        // Single delete execution
        await axiosInstance.post("admin/user/delete/", { userId: studentId });
        toast.success("Student removed successfully");
      }
      setConfirmDelete(false);
      fetch();
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error("Failed to delete student records");
      setConfirmDelete(false);
    }
  };

  const handleAddStudent = () => {
    setModal(true);
    setEditStudentData([]);
  };

  const handleEditStudent = (data) => {
    setModal(true);
    setEditStudentData(data);
  };

  const handleStatusFilter = (e) => {
    setFilter({ ...filter, status: e.target.value, page: 1 });
  };

  const handleStudentList = async () => {
    try {
      const res = await axiosInstance.get("/admin/export/students");
      downloadCSV(res?.data, "Student-list");
      toast.success("CSV export downloaded successfully");
    } catch (error) {
      console.error("Error downloading student list:", error);
      toast.error("CSV export failed");
    }
  };

  // 1. Process Filtering Pipeline (Tab + Dropdown + Search + Dates)
  const processedStudents = studentData.filter((student) => {
    // Tab Filter
    if (activeFilter === "ongoing" && student.isActive !== 1) return false;
    if (activeFilter === "passed" && student.isActive !== 0) return false;

    // Status Dropdown Filter
    if (filter.status !== "") {
      const activeVal = Number(filter.status); // 1 = Active, 0 = Inactive
      if (student.isActive !== activeVal) return false;
    }

    // Search Query Filter
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      const fullName = `${student.firstName || ""} ${student.lastName || ""}`.toLowerCase();
      const email = (student.email || "").toLowerCase();
      const username = (student.userName || "").toLowerCase();
      const phone = (student.phoneNumber || "").toLowerCase();
      const id = String(student.id || "");
      if (
        !fullName.includes(query) &&
        !email.includes(query) &&
        !username.includes(query) &&
        !phone.includes(query) &&
        !id.includes(query)
      ) {
        return false;
      }
    }

    // Date Range Filter
    if (filter.startDate && filter.endDate) {
      const regDate = moment(student.createdAt);
      const start = moment(filter.startDate);
      const end = moment(filter.endDate).endOf("day");
      if (!regDate.isBetween(start, end, null, "[]")) {
        return false;
      }
    }

    return true;
  });

  // 2. Slicing logic for correct client-side pagination
  const startIndex = (filter.page - 1) * filter.itemsPerPage;
  const endIndex = startIndex + filter.itemsPerPage;
  const paginatedStudents = processedStudents.slice(startIndex, endIndex);

  // Row selection checkbox handlers
  const handleToggleSelectStudent = (id) => {
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    const paginatedIds = paginatedStudents.map(s => s.id);
    const allSelected = paginatedIds.every(id => selectedStudentIds.has(id));

    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        paginatedIds.forEach(id => next.delete(id));
      } else {
        paginatedIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    setStudentId("bulk");
    setConfirmDelete(true);
  };

  const handleBulkEmail = () => {
    const emails = Array.from(selectedStudentIds)
      .map(id => studentData.find(s => s.id === id)?.email)
      .filter(Boolean)
      .join(",");
    
    if (emails) {
      window.location.href = `mailto:${emails}`;
    }
  };

  // Stat summary configuration
  const statsConfig = [
    {
      name: "Total Students",
      total: studentStats?.totalStudents || studentData.length,
      icon: <Users className="w-5 h-5 text-slate-500" />,
      bg: "bg-slate-50"
    },
    {
      name: "Active Students",
      total: studentStats?.ongoingStudents || studentData.filter(s => s.isActive === 1).length,
      icon: <UserCheck className="w-5 h-5 text-slate-500" />,
      bg: "bg-slate-50"
    },
    {
      name: "Passed Out Students",
      total: studentStats?.passedOutStudents || studentData.filter(s => s.isActive === 0).length,
      icon: <GraduationCap className="w-5 h-5 text-slate-500" />,
      bg: "bg-slate-50"
    },
    {
      name: "Recently Joined",
      total: studentStats?.recentlyJoined || 0,
      icon: <Clock className="w-5 h-5 text-slate-500" />,
      bg: "bg-slate-50"
    }
  ];

  return (
    <div className="width-0" style={{ fontFamily: "'Inter', sans-serif" }}>
      {loading ? (
        <div className="min-h-[400px] flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 md:p-8 shadow-3xs max-w-7xl mx-auto">
          {/* Header Directory */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 border-b border-slate-100 pb-6">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-800">Student Directory</h1>
              <p className="text-slate-500 text-sm mt-1">Manage enrollments, track stats, export records, and perform actions.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleStudentList}
                className="flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-800 font-semibold text-xs px-3.5 py-2.5 rounded-xl shadow-3xs transition-all cursor-pointer"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
              {/* <button
                onClick={() => setAddBulkUsers(true)}
                className="flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50 text-slate-600 hover:text-slate-800 font-semibold text-xs px-3.5 py-2.5 rounded-xl shadow-3xs transition-all cursor-pointer"
              >
                <FileSpreadsheet className="w-4 h-4" /> Bulk Upload
              </button> */}
              {/* <button
                onClick={handleAddStudent}
                className="flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white font-semibold text-xs px-3.5 py-2.5 rounded-xl shadow-3xs transition-all cursor-pointer"
              >
                <Plus className="w-4 h-4" /> Add Student
              </button> */}
            </div>
          </div>

          {/* Stats Summaries Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            {statsConfig.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-3xs hover:shadow-2xs transition-all duration-300 flex items-center justify-between group cursor-pointer hover:border-slate-300"
              >
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{item.name}</p>
                  <p className="text-2xl font-black text-slate-800 mt-1">{item.total}</p>
                </div>
                <div className={`p-3 rounded-xl border border-slate-100 group-hover:scale-105 transition-transform duration-300 ${item.bg}`}>
                  {item.icon}
                </div>
              </div>
            ))}
          </div>

          {/* Advanced Controls Header */}
          <div className="bg-slate-50/50 border border-slate-200/80 rounded-xl p-4 mb-6 shadow-3xs flex flex-col gap-4">
            
            {/* Top row: Tab Switcher & Search Bar */}
            <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4">
              
              {/* Tab Navigation */}
              <div className="flex border border-slate-200 rounded-lg p-0.5 bg-slate-100/80 self-start text-xs font-semibold">
                <button
                  onClick={() => { setActiveFilter("all"); setFilter({ ...filter, page: 1 }); }}
                  className={`px-4 py-1.5 rounded-md transition-all ${activeFilter === "all" ? "bg-white text-slate-800 shadow-3xs font-bold" : "text-slate-500 hover:text-slate-700"}`}
                >
                  All Students
                </button>
                <button
                  onClick={() => { setActiveFilter("ongoing"); setFilter({ ...filter, page: 1 }); }}
                  className={`px-4 py-1.5 rounded-md transition-all ${activeFilter === "ongoing" ? "bg-white text-slate-800 shadow-3xs font-bold" : "text-slate-500 hover:text-slate-700"}`}
                >
                  Active / Ongoing
                </button>
                <button
                  onClick={() => { setActiveFilter("passed"); setFilter({ ...filter, page: 1 }); }}
                  className={`px-4 py-1.5 rounded-md transition-all ${activeFilter === "passed" ? "bg-white text-slate-800 shadow-3xs font-bold" : "text-slate-500 hover:text-slate-700"}`}
                >
                  Inactive / Passed
                </button>
              </div>

              {/* Real-time search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search name, ID, username, email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent text-sm transition-all"
                />
              </div>

            </div>

            {/* Bottom Row: Dynamic Filters (Dates, Status select, Page count) */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-4 pt-3 border-t border-slate-100">
              
              <div className="flex flex-wrap items-center gap-3.5">
                {/* Date range picker input */}
                <div className="relative" ref={refOne}>
                  <div 
                    onClick={() => {
                      setDateRangePicker(true);
                      setDateToggle(!dateToggle);
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg bg-white text-slate-700 hover:bg-slate-50 transition-all cursor-pointer text-xs font-semibold select-none shadow-3xs"
                  >
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>
                      {dateRangePicker 
                        ? `${moment(range[0].startDate).format("MM/DD/YY")} - ${moment(range[0].endDate).format("MM/DD/YY")}`
                        : "Filter by Date Range"
                      }
                    </span>
                    {dateRangePicker ? (
                      <span onClick={clearDateFilter} className="ml-1 p-0.5 hover:bg-slate-200 rounded-full text-slate-400 hover:text-slate-600 transition-colors">
                        <X className="w-3 h-3" />
                      </span>
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 ml-1" />
                    )}
                  </div>

                  {dateToggle && (
                    <div className="absolute left-0 mt-2 z-50 shadow-xl rounded-xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      <DateRange
                        onChange={(item) => handleDateRangeFilter(item)}
                        editableDateInputs={true}
                        moveRangeOnFirstSelection={false}
                        ranges={range}
                        months={1}
                        direction="horizontal"
                        className="dateElement bg-white"
                      />
                    </div>
                  )}
                </div>

                {/* Select Status */}
                <select
                  value={filter.status}
                  onChange={handleStatusFilter}
                  className="!w-40 !py-1.5 !pl-3 !pr-8 !rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition-all cursor-pointer text-xs font-semibold shadow-3xs !appearance-auto"
                >
                  <option value="">All Statuses</option>
                  <option value="1">Active Only</option>
                  <option value="0">Inactive Only</option>
                </select>

                {/* Items per page selector */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-slate-400 font-medium">Page Size</span>
                  <select
                    value={filter.itemsPerPage}
                    onChange={handleSelectChange}
                    className="!w-20 !py-1.5 !pl-2 !pr-6 !rounded-lg border border-slate-200 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition-all cursor-pointer text-xs font-semibold shadow-3xs !appearance-auto"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Status indicators count */}
              <div className="text-xs text-slate-400 font-medium text-right self-center">
                Showing {startIndex + 1} - {Math.min(endIndex, processedStudents.length)} of {processedStudents.length} entries
              </div>

            </div>

          </div>

          {/* Enrolled Students Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-3xs mb-6 relative">
            {processedStudents.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-slate-300 border border-slate-100 mb-4">
                  <User className="w-6 h-6" />
                </div>
                <h3 className="text-sm font-bold text-slate-800">No Students Found</h3>
                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">No records match the active search queries or filters.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-400 text-xs font-bold uppercase tracking-wider select-none">
                      {/* Checkbox column */}
                      <th className="py-4 px-4 w-12">
                        <button
                          onClick={handleToggleSelectAll}
                          className="text-slate-400 hover:text-slate-600 transition-colors"
                          title="Select all paginated rows"
                        >
                          {paginatedStudents.every(s => selectedStudentIds.has(s.id)) ? (
                            <CheckSquare className="w-4.5 h-4.5 text-slate-800" />
                          ) : (
                            <Square className="w-4.5 h-4.5" />
                          )}
                        </button>
                      </th>
                      <th className="py-4 px-3 w-16">Sr.No</th>
                      <th className="py-4 px-4">Student Name</th>
                      <th className="py-4 px-4">Student ID</th>
                      <th className="py-4 px-4">User Name</th>
                      <th className="py-4 px-4">Email</th>
                      <th className="py-4 px-4">Phone</th>
                      <th className="py-4 px-4">Register Date</th>
                      <th className="py-4 px-4">Status</th>
                      <th className="py-4 px-4 text-right w-16">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700 text-sm">
                    {paginatedStudents.map((ele, index) => {
                      const isSelected = selectedStudentIds.has(ele.id);
                      return (
                        <tr key={index} className={`hover:bg-slate-50/40 transition-all ${isSelected ? "bg-slate-50/60" : ""}`}>
                          
                          {/* Checkbox cell */}
                          <td className="py-3.5 px-4">
                            <button
                              onClick={() => handleToggleSelectStudent(ele.id)}
                              className="text-slate-400 hover:text-slate-600 transition-colors"
                            >
                              {isSelected ? (
                                <CheckSquare className="w-4.5 h-4.5 text-slate-800" />
                              ) : (
                                <Square className="w-4.5 h-4.5" />
                              )}
                            </button>
                          </td>

                          {/* Index cell */}
                          <td className="py-3.5 px-3 font-medium text-slate-400">{startIndex + index + 1}</td>

                          {/* Name and avatar cell */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-3">
                              {ele?.profileImage ? (
                                <img alt="Profile" src={ele?.profileImage} className="w-8 h-8 rounded-full object-cover border border-slate-200" />
                              ) : (
                                <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-bold flex items-center justify-center text-xs">
                                  {ele?.firstName ? ele.firstName.charAt(0).toUpperCase() : "?"}
                                </span>
                              )}
                              <p className="font-semibold text-slate-800">{`${ele.firstName || ""} ${ele.lastName || ""}`}</p>
                            </div>
                          </td>

                          <td className="py-3.5 px-4 font-mono text-xs">{ele.id}</td>
                          <td className="py-3.5 px-4 font-medium">{ele.userName}</td>
                          <td className="py-3.5 px-4 text-slate-500">{ele.email}</td>
                          <td className="py-3.5 px-4 text-slate-500">{ele.phoneNumber || "-"}</td>
                          <td className="py-3.5 px-4 text-slate-500">
                            {ele.createdAt ? moment(ele.createdAt).format("DD/MM/YYYY") : "N/A"}
                          </td>

                          {/* Status Badge cell */}
                          <td className="py-3.5 px-4">
                            {ele.isActive === 1 ? (
                              <span className="inline-flex bg-slate-800 text-white text-[10px] font-bold px-2 py-0.5 rounded-full select-none">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex bg-slate-100 text-slate-500 border border-slate-200 text-[10px] font-bold px-2 py-0.5 rounded-full select-none">
                                Inactive
                              </span>
                            )}
                          </td>

                          {/* Actions trigger cell */}
                          <td className="py-3.5 px-4 text-right relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenActionRowId(openActionRowId === ele.id ? null : ele.id);
                              }}
                              className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700 transition-colors"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            {/* Dropdown popup */}
                            {openActionRowId === ele.id && (
                              <div className="absolute right-6 mt-1 w-40 bg-white border border-slate-200/80 rounded-xl shadow-lg py-1 z-50 text-left animate-in fade-in zoom-in-95 duration-100">
                                <button
                                  onClick={() => {
                                    handleEditStudent(ele);
                                    setOpenActionRowId(null);
                                  }}
                                  className="w-full px-4 py-2 hover:bg-slate-50 text-slate-700 font-semibold text-xs flex items-center gap-2"
                                >
                                  <Edit3 className="w-3.5 h-3.5 text-slate-400" /> Edit Record
                                </button>
                                <button
                                  onClick={() => {
                                    deleteStudent(ele.id);
                                    setOpenActionRowId(null);
                                  }}
                                  className="w-full px-4 py-2 hover:bg-slate-50 text-rose-600 font-semibold text-xs flex items-center gap-2 border-b border-slate-100"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-rose-400" /> Remove User
                                </button>
                                <a
                                  href={`mailto:${ele.email}`}
                                  className="w-full px-4 py-2 hover:bg-slate-50 text-slate-700 font-semibold text-xs flex items-center gap-2"
                                >
                                  <Mail className="w-3.5 h-3.5 text-slate-400" /> Send Email
                                </a>
                              </div>
                            )}
                          </td>

                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {processedStudents.length > 0 && (
            <div className="flex justify-center py-4">
              <Pagination
                className="student-list-pagination"
                currentPage={filter.page}
                totalCount={processedStudents.length}
                pageSize={filter.itemsPerPage}
                onPageChange={(page) => setFilter({ ...filter, page: page })}
              />
            </div>
          )}

          {/* Floating Bulk Actions Drawer */}
          {selectedStudentIds.size > 0 && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3.5 rounded-xl shadow-xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-300">
                {selectedStudentIds.size} student(s) selected
              </span>
              <div className="h-4 w-px bg-slate-800" />
              <div className="flex gap-4">
                <button
                  onClick={handleBulkEmail}
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-300 hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4" /> Send Email
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-rose-400 hover:text-rose-300 transition-colors"
                >
                  <Trash2 className="w-4 h-4" /> Remove Users
                </button>
              </div>
              <div className="h-4 w-px bg-slate-800" />
              <button
                onClick={() => setSelectedStudentIds(new Set())}
                className="text-slate-400 hover:text-white p-0.5 rounded-full hover:bg-slate-800 transition-all"
                title="Cancel selection"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Modal - Delete Confirmation */}
          {confirmDelete === true && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm">
              <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-xl border border-slate-100 animate-in fade-in zoom-in duration-200">
                <div className="flex items-center gap-3 text-amber-500 mb-4">
                  <AlertTriangle className="w-6 h-6" />
                  <h3 className="text-lg font-bold text-slate-800">Warning!</h3>
                </div>
                <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                  {studentId === "bulk" 
                    ? `Are you sure you want to remove the ${selectedStudentIds.size} selected students? This action is permanent and cannot be undone.`
                    : CourseBuilderStaticData.deleteStudentOverlay.header
                  }
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 rounded-lg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteStudent}
                    className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-all"
                  >
                    Yes, Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal - Add / Edit Student Form */}
          {modal && (
            <Modal isOpen={modal} onClose={() => setModal(false)}>
              <div className="p-5" style={{ minWidth: "320px" }}>
                <h2 className="text-xl font-bold mb-4 text-slate-800 border-b border-slate-100 pb-2">
                  {editStudentData.id ? "Edit Student Record" : "Add New Student"}
                </h2>
                <Forms
                  setModal={setModal}
                  title="Student"
                  role={2}
                  editData={editStudentData}
                />
              </div>
            </Modal>
          )}

          {/* Modal - Bulk Upload Overlay */}
          {addBulkUsers && (
            <Modal isOpen={addBulkUsers} onClose={() => setAddBulkUsers(false)}>
              <div className="p-5" style={{ minWidth: "400px" }}>
                <h2 className="text-xl font-bold mb-4 text-slate-800 border-b border-slate-100 pb-2">Bulk Import Students</h2>
                <BulkUploadOverlay
                  onCancel={() => setAddBulkUsers(false)}
                  role={2}
                  setIsUploaded={setIsUploaded}
                />
              </div>
            </Modal>
          )}

        </div>
      )}
    </div>
  );
}

export default InstructorStudentList;
